import { Gap } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { MouseEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router';
import {
  navigateWithAuthTransition,
  shouldHandleTransitionLinkClick,
} from '../../auth.navigation';
import { authPath } from '../../model/auth.paths.model';
import {
  authVmCopies,
  type RegisterEmailStepValues,
  type RegisterStepFormState,
  type RegisterStepSharedProps,
  type SsoProviderState,
} from '../../view-model/register.types';
import GoogleLogoSvg from '../assets/google-logo.svg?react';
import AppleLogoSvg from '../assets/apple-logo.svg?react';
import { AuthLayout } from '../components/AuthLayout';
import {
  AuthClientAppHint,
  AuthTermsNotice,
} from '../components/AuthFooterText';
import * as styles from '../register.view.css';
import {
  StepButton,
  StepForm,
  StepInput,
  StepTitle,
  toSignupFormId,
  toStepSubmitState,
  toSignupTestId,
} from './StepFormPrimitives';

const REGISTER_INLINE_INVALID_EMAIL_MESSAGE =
  'Please enter a valid email address.';

type EmailStepProps = {
  values: RegisterEmailStepValues;
  formState: RegisterStepFormState<'email'>;
  testimonialsContent: ReactNode;
  googleSsoState: SsoProviderState;
  appleSsoState: SsoProviderState;
} & Pick<
  RegisterStepSharedProps,
  'canContinue' | 'shouldAutoFocusInput' | 'dispatch'
>;

export const EmailStep = (p: EmailStepProps) => {
  const submitState = toStepSubmitState(p.formState);
  const signInPath = authPath('/login');
  const [isEmailTouched, setIsEmailTouched] = useState(false);
  const isGoogleSsoLoading = p.googleSsoState.type === 'loading';
  const googleSsoError =
    p.googleSsoState.type === 'error' ? p.googleSsoState.message : null;
  const isAppleSsoLoading = p.appleSsoState.type === 'loading';
  const appleSsoError =
    p.appleSsoState.type === 'error' ? p.appleSsoState.message : null;
  const setValue = <TField extends keyof RegisterEmailStepValues>(
    field: TField,
    value: RegisterEmailStepValues[TField],
  ): void => {
    p.dispatch('setField', {
      step: 'email',
      field,
      value,
    });
  };

  const handleSignInClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!shouldHandleTransitionLinkClick(event)) {
      return;
    }

    event.preventDefault();
    navigateWithAuthTransition(signInPath);
  };

  const hasInvalidTouchedEmail =
    isEmailTouched &&
    p.values.email.trim().length > 0 &&
    !p.canContinue &&
    submitState.type !== 'error';
  const emailErrorMessage =
    submitState.type === 'error'
      ? submitState.message
      : hasInvalidTouchedEmail
        ? REGISTER_INLINE_INVALID_EMAIL_MESSAGE
        : null;
  const emailInputWrapperClassName = emailErrorMessage
    ? styles.authInputWrapperError
    : styles.authInputWrapper;

  return (
    <AuthLayout
      preset="register-two-column"
      rightColumn={p.testimonialsContent}
      content={
        <div className={styles.codeStepStack}>
          <StepTitle>
            <>Become a creator on Playbook</>
          </StepTitle>
          <Gap height="3xl" />
          <StepForm
            id={toSignupFormId('email')}
            step="email"
            onSubmit={() => {
              p.dispatch('continue');
            }}
          >
            <StepInput
              variant="email"
              autoFocus={p.shouldAutoFocusInput}
              placeholder={authVmCopies.common.emailAddressPlaceholder}
              value={p.values.email}
              testId={toSignupTestId('email', 'email', 'input')}
              error={emailErrorMessage ?? undefined}
              hideError
              inputWrapperClassName={emailInputWrapperClassName}
              onValueChange={(value) => {
                setIsEmailTouched(false);
                setValue('email', value);
              }}
              onBlur={() => setIsEmailTouched(true)}
              disabled={submitState.type === 'submitting'}
            />
            <Typography
              variant="body"
              size="small"
              className={styles.emailSupportingText}
            >
              This email will be used to log in. No password needed.
            </Typography>

            {emailErrorMessage ? (
              <Typography
                role="alert"
                variant="body"
                size="small"
                color="ERROR-100"
                className={styles.emailErrorText}
              >
                {emailErrorMessage}
              </Typography>
            ) : null}

            <Gap height="3xl" />
            <StepButton
              testId={toSignupTestId('email', 'create-account', 'button')}
              loading={submitState.type === 'submitting'}
              disabled={!p.canContinue || submitState.type === 'submitting'}
            >
              Create account
            </StepButton>
          </StepForm>

          <Gap height="l" />
          <div className={styles.orSeparator}>
            <div className={styles.orSeparatorLine} />
            <span className={styles.orSeparatorText}>or</span>
            <div className={styles.orSeparatorLine} />
          </div>
          <Gap height="l" />

          <div className={styles.ssoButtonsStack}>
            <button
              type="button"
              data-testid="signup-google-sso-button"
              className={styles.googleSsoButton}
              disabled={isGoogleSsoLoading}
              onClick={() => p.dispatch('googleSso')}
            >
              <GoogleLogoSvg className={styles.googleSsoIcon} />
              {isGoogleSsoLoading ? 'Signing in…' : 'Sign in with Google'}
            </button>

            <button
              type="button"
              data-testid="signup-apple-sso-button"
              className={styles.appleSsoButton}
              disabled={isAppleSsoLoading}
              onClick={() => p.dispatch('appleSso')}
            >
              <AppleLogoSvg className={styles.appleSsoIcon} />
              {isAppleSsoLoading ? 'Signing in…' : 'Sign in with Apple'}
            </button>
          </div>

          {googleSsoError ? (
            <Typography
              role="alert"
              variant="body"
              size="small"
              className={styles.ssoError}
            >
              {googleSsoError}
            </Typography>
          ) : null}

          {appleSsoError ? (
            <Typography
              role="alert"
              variant="body"
              size="small"
              className={styles.ssoError}
            >
              {appleSsoError}
            </Typography>
          ) : null}
        </div>
      }
      footer={
        <div className={styles.entryFooter}>
          <AuthTermsNotice
            textClassName={styles.entryTosText}
            linkClassName={styles.entryTosLink}
          />

          <div className={styles.footerDivider} />

          <Typography
            variant="body"
            size="medium"
            className={styles.entryAccountText}
          >
            Already have an account?{' '}
            <Link
              to={signInPath}
              className={styles.entryAccountLink}
              data-testid={toSignupTestId('email', 'signin', 'link')}
              onClick={handleSignInClick}
            >
              Sign in
            </Link>
          </Typography>

          <AuthClientAppHint
            hintClassName={styles.bottomHint}
            linkClassName={styles.textLink}
          />
        </div>
      }
    />
  );
};
